/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import {
	BarSeriesOption,
	EChartsOption,
	LineSeriesOption,
	LinesSeriesOption,
	MapSeriesOption,
	PieSeriesOption,
	ScatterSeriesOption,
	SeriesOption
} from "echarts";
import {
	CallbackDataParams,
	TooltipOption,
	TopLevelFormatterParams
} from "echarts/types/dist/shared";
import { takeUntil, tap } from "rxjs";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import { MapChart } from "../models/http/charts/map-chart/map-chart.model";
import { MixedChart } from "../models/http/charts/mixed-chart/mixed-chart.model";
import { PieChart } from "../models/http/charts/pie-chart/pie-chart.model";
import { ScatterChart } from "../models/http/charts/scatter-chart/scatter-chart.model";
import { CategoriesSource } from "../models/ui/categories-source.model";
import { ChartPattern, ChartType, SerieDataWithThreshold } from "../models/ui/charts.model";
import { TooltipLabel } from "../models/ui/tooltip-label.model";
import { COLORS, REGIONS_MAP } from "../utils/constants";
import { DEFAULT_CHART_OPTIONS } from "../utils/echarts/chart-defaults";
import {
	buildScatterMarkLineOptions,
	getBarColor,
	getChartTooltip,
	getMappedScatterPoint,
	getMapSelectedItemAreaColor,
	getPieChartLabelColor,
	getPieChartLabelPosition,
	getPieChartTotalValue,
	getRangesColorForMapItemAreaColor
} from "../utils/echarts/chart-utils";
import {
	deepClone,
	overrideObject,
	prettifyISODate,
	reverseConditionally,
	roundValueUpTo
} from "../utils/utils";
import { DashboardHardwareAssetsService } from "./dashboard-hardware-assets.service";
import { AppStateService } from "./state/app-state.service";

@Injectable({
	providedIn: "root"
})
export class ChartService {
	private _symbolShapes = ["circle", "rect", "triangle", "diamond"];
	private _isAppAccessible: boolean = false;
	private _regionsMap: Record<string, string> = REGIONS_MAP;

	constructor(
		private _appStateService: AppStateService,
		private _dashboardHardwareAssetsService: DashboardHardwareAssetsService
	) {
		this._appStateService.isAppAccessible$
			.pipe(
				tap({
					next: isAppAccessible => {
						this._isAppAccessible = isAppAccessible;
					}
				}),
				takeUntil(this._appStateService.isAppDestroyed$)
			)
			.subscribe();

		this._dashboardHardwareAssetsService
			.getAllCountries()
			.pipe(
				tap({
					next: countries =>
						(this._regionsMap = countries.reduce(
							(acc, country) => ({ ...acc, [country.value]: country.label }),
							this._regionsMap
						))
				})
			)
			.subscribe();
	}

	/**
	 * Generates chart options for ECharts based on the provided parameters.
	 *
	 * @param chartType - The type of chart to generate options for.
	 * @param data - The data to be used in the chart.
	 * @param configOverrides - Optional overrides for the default chart options.
	 * @param isGradient - Whether to apply gradient colors to the chart.
	 * @param colors - An array of colors to be used in the chart.
	 * @param chartPatterns - An array of chart patterns to be applied.
	 * @param isLegendVisible - Whether the legend should be visible.
	 * @param tooltipLabel - The type of label to be used in the tooltip ("value" or "serie").
	 * @returns The generated ECharts options.
	 */
	public getChartOptions(
		chartType: ChartType,
		data: unknown,
		configOverrides: EChartsOption = {},
		isGradient: boolean,
		isGradientInverted: boolean,
		colors: string[],
		chartPatterns: ChartPattern[],
		isLegendVisible: boolean,
		tooltipLabel: TooltipLabel,
		categoriesSource?: CategoriesSource,
		labelPosition?: "outside" | "inside",
		useGrouping: boolean = false
	): EChartsOption {
		const overrides = deepClone(configOverrides);
		const categories = this._getCategories(chartType, data, overrides, categoriesSource);

		if (useGrouping) {
			overrides.tooltip = {
				...((overrides.tooltip as object) || {}),
				formatter: (params: TopLevelFormatterParams) => getChartTooltip(params, true)
			};
		}

		overrides.legend = {
			...(overrides.legend || {}),
			show: isLegendVisible,
			data: this._getLegendData(data, chartType, colors)
		};

		if (chartType === "line" && isLegendVisible) {
			overrides.legend = {
				type: "scroll",
				top: 0,
				left: 0,
				right: 0,
				...(overrides.legend || {})
			};

			overrides.grid = {
				...(overrides.grid || {}),
				top: !Array.isArray(overrides.grid) ? (overrides.grid?.top ?? 60) : 60
			};
		}

		overrides.aria = {
			...(overrides.aria || {}),
			...(chartPatterns.length && {
				decal: {
					show: this._isAppAccessible,
					decals: chartPatterns.map(pattern => pattern ?? { symbol: "none" })
				}
			})
		};

		if (chartType === "barY") {
			overrides.grid = {
				containLabel: false,
				left: 180
			};

			overrides.yAxis = {
				...(overrides.yAxis || {}),
				axisLabel: {
					width: 124,
					overflow: "truncate",
					align: "right",
					formatter: v => v
				},
				tooltip: {
					show: true,
					formatter: (v: CallbackDataParams) =>
						v.value
							? `<div class="echart-axis-label-tooltip-container">
						<div class="echarts-tooltip-value">
							${v.value}
						</div>
					</div>`
							: "-"
				} as TooltipOption,
				type: "category",
				data: categories
			};
			overrides.xAxis = {
				name: this._getNameFromData(chartType, data),
				...(overrides.xAxis || {})
			};
		} else {
			if (chartType !== "map" && chartType !== "pie" && chartType !== "scatter") {
				overrides.xAxis = {
					...(overrides.xAxis || {}),
					type: "category",
					data: categories
				};
				overrides.yAxis = {
					name: this._getNameFromData(chartType, data),
					...(overrides.yAxis || {})
				};
			}
		}

		const coreOptions = overrideObject(
			DEFAULT_CHART_OPTIONS[chartType](),
			overrides
		) as EChartsOption;
		const normalizedSeriesOverrides = this._normalizeSeriesOverrides(
			chartType,
			coreOptions.series
		);

		const series = this._getSeries(
			chartType,
			data,
			normalizedSeriesOverrides,
			isGradient,
			isGradientInverted,
			colors,
			chartPatterns,
			tooltipLabel,
			labelPosition
		);

		return {
			...coreOptions,
			series
		};
	}

	private _normalizeSeriesOverrides(
		chartType: ChartType,
		seriesOverrides: SeriesOption | SeriesOption[] | undefined
	): SeriesOption | SeriesOption[] | undefined {
		if (!Array.isArray(seriesOverrides)) {
			return seriesOverrides;
		}

		const defaultSeries = DEFAULT_CHART_OPTIONS[chartType]().series;

		if (Array.isArray(defaultSeries) || !defaultSeries) {
			return seriesOverrides;
		}

		return seriesOverrides.map(seriesOverride =>
			overrideObject(deepClone(defaultSeries), seriesOverride)
		);
	}

	/**
	 * Generates legend data for a chart based on the provided data, chart type, and color palette.
	 *
	 * @param data - The raw data used to extract series names for the legend.
	 * @param chartType - The type of chart for which the legend is being generated.
	 * @param colors - An array of color strings to assign to each legend item.
	 * @returns An array of legend items, each containing a series name and its associated color style.
	 */
	private _getLegendData(
		data: unknown,
		chartType: ChartType,
		colors: string[]
	): { name: string; itemStyle: { color: string } }[] {
		const series = this._getSeriesNames(data, chartType);

		return series.map((name, index) => ({
			name,
			itemStyle: { color: colors[index] }
		}));
	}

	/**
	 * Retrieves the series names from the provided chart data based on the specified chart type.
	 *
	 * @param data - The chart data object, whose structure depends on the chart type.
	 * @param chartType - The type of chart ('line', 'barX', 'barY', or 'mixed') used to determine how to extract series names.
	 * @returns An array of series names extracted from the chart data. Returns an empty array if the chart type is unrecognized.
	 */
	private _getSeriesNames(data: unknown, chartType: ChartType): string[] {
		switch (chartType) {
			case "line":
				return (data as LineChart).lineSeries.map(serie => serie.name);
			case "barX":
			case "barY":
				return (data as BarChart).barSeries.map(serie => serie.name);
			case "mixed":
				return (data as MixedChart).series.map(serie => serie.name);
			default:
				return [];
		}
	}

	/**
	 * Retrieves the unit name from the provided chart data based on the chart type.
	 *
	 * @param chartType - The type of the chart (e.g., "line", "barX", "barY").
	 * @param data - The chart data object, which should match the structure of the specified chart type.
	 * @returns The unit name as a string if available; otherwise, an empty string.
	 */
	private _getNameFromData(chartType: string, data: unknown): string {
		switch (chartType) {
			case "line":
				return (data as LineChart).lineSeries[0]?.linePoints[0]?.unit;
			case "barX":
			case "barY":
				return (data as BarChart).barSeries[0]?.bars[0]?.unit;
			default:
				return "";
		}
	}

	/**
	 * Retrieves the categories for a given chart type from the provided data.
	 *
	 * @private
	 * @param {string} chartType - The type of the chart (e.g., "line", "barX", "barY").
	 * @param {unknown} data - The data object containing the chart information.
	 * @returns  An array of category strings based on the chart type.
	 */
	private _getCategories(
		chartType: string,
		data: unknown,
		overrides: EChartsOption,
		categoriesSource?: CategoriesSource
	): string[] {
		switch (chartType) {
			case "line":
				return this._getLineCategories(data as LineChart);
			case "barX":
			case "barY":
				return this._getBarCategories(
					data as BarChart,
					overrides,
					chartType === "barY",
					categoriesSource
				);
			case "mixed":
				return this._getMixedCategories(data as MixedChart);
			default:
				return [];
		}
	}

	/**
	 * Extracts and returns an array of category names from the given LineChart data.
	 *
	 * @param data - The LineChart data containing line series and points.
	 * @returns An array of category names extracted from the line points.
	 */
	private _getLineCategories(data: LineChart): string[] {
		if (!data.lineSeries.length) return [];

		const allDates = new Set<string>();
		data.lineSeries.forEach(serie =>
			serie.linePoints
				.filter(point => Object.keys(point).length > 0)
				.forEach(point => allDates.add(point.name))
		);

		return this._sortLineCategories([...allDates]).map(date =>
			date.includes("T") ? prettifyISODate(date) : date
		);
	}

	/**
	 * Retrieves the categories for a bar chart based on the provided data and overrides.
	 *
	 * @param data - The bar chart data containing series and bars information.
	 * @param overrides - The ECharts options that may override the default behavior.
	 * @param isBarY - A boolean indicating whether the bar chart is oriented vertically (true) or horizontally (false).
	 * @returns An array of category names for the bar chart.
	 */
	private _getBarCategories(
		data: BarChart,
		overrides: EChartsOption,
		isBarY: boolean,
		categoriesSource?: CategoriesSource
	): string[] {
		const isStackedBarChart =
			!!overrides?.series && Array.isArray(overrides.series) && overrides.series.length
				? !!("stack" in (overrides.series[0] as BarSeriesOption[]))
				: !!overrides?.series && !!("stack" in (overrides.series as BarSeriesOption));

		return categoriesSource &&
			categoriesSource === "series" &&
			data.barSeries.length > 1 &&
			!isStackedBarChart
			? data.barSeries.map(serie => serie.name)
			: data.barSeries.length
				? reverseConditionally(
						data.barSeries[0].bars
							.filter(bar => Object.keys(bar).length > 0)
							.map(bar => bar.name),
						isBarY
					)
				: [];
	}

	/**
	 * Extracts the category names from the first series of a mixed chart data object.
	 * If the series contains more than one item, it filters out any empty objects
	 * and maps the remaining objects to their 'name' property.
	 *
	 * @param data - The mixed chart data object containing series and values.
	 * @returns An array of category names from the first series, or an empty array if there is only one series.
	 */
	private _getMixedCategories(data: MixedChart): string[] {
		return data.series.length > 1
			? data.series[0].values
					.filter(value => Object.keys(value).length > 0)
					.map(value => value.name)
			: [];
	}

	/**
	 * Generates the series options for a given chart type.
	 *
	 * @param chartType - The type of the chart (e.g., "line", "barX", "barY", "pie", "scatter", "map", "mixed").
	 * @param data - The data to be used for the chart.
	 * @param seriesOverrides - Optional series options to override the default settings.
	 * @param isGradient - Indicates whether the chart should use gradient colors.
	 * @param colors - An array of colors to be used in the chart.
	 * @param patterns - An array of patterns to be used in the chart.
	 * @param tooltipLabel - Specifies the label to be used in the tooltip ("value" or "serie").
	 * @returns An array of series options for the specified chart type.
	 */
	private _getSeries(
		chartType: string,
		data: unknown,
		seriesOverrides: SeriesOption | SeriesOption[] | undefined,
		isGradient: boolean,
		isGradientInverted: boolean,
		colors: string[],
		patterns: ChartPattern[],
		tooltipLabel: TooltipLabel,
		labelPosition?: "outside" | "inside"
	): SeriesOption[] {
		switch (chartType) {
			case "line":
				return this._getSeriesForLine(data as LineChart, seriesOverrides, tooltipLabel);
			case "barX":
			case "barY":
				return this._getSeriesForBar(
					data as BarChart,
					seriesOverrides,
					isGradient,
					isGradientInverted,
					colors,
					patterns,
					chartType === "barY",
					tooltipLabel
				);
			case "pie":
				return this._getSeriesForPie(
					data as PieChart,
					seriesOverrides,
					colors,
					labelPosition
				);
			case "scatter":
				return this._getSeriesForScatter(data as ScatterChart, seriesOverrides);
			case "map":
				return this._getSeriesForMap(data as MapChart, seriesOverrides);
			case "mixed":
				return this._getSeriesForMixed(
					data as MixedChart,
					seriesOverrides,
					colors,
					patterns
				);
			default:
				return [];
		}
	}

	/**
	 * Generates an array of series options for a line chart.
	 *
	 * @param lineChart - The line chart data containing series and points.
	 * @param coreSerieOptions - The core options to be applied to each series.
	 * @param tooltipLabel - The type of label to be used in the tooltip ("value" or "serie").
	 * @returns An array of series options configured for the line chart.
	 */
	private _getSeriesForLine(
		lineChart: LineChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined,
		tooltipLabel: TooltipLabel
	): SeriesOption[] {
		const allDates = new Set<string>();
		lineChart.lineSeries.forEach(serie =>
			serie.linePoints
				.filter(point => Object.keys(point).length > 0)
				.forEach(point => allDates.add(point.name))
		);
		const sortedDates = this._sortLineCategories([...allDates]);

		return lineChart.lineSeries.map((serie, index) => {
			const pointsByDate = new Map(
				serie.linePoints
					.filter(point => Object.keys(point).length > 0)
					.map(point => [point.name, point])
			);

			return {
				...(Array.isArray(coreSerieOptions)
					? (coreSerieOptions?.[index] as LineSeriesOption[])
					: (coreSerieOptions as LineSeriesOption)),
				name: serie.name,
				data: sortedDates.map(date => {
					const point = pointsByDate.get(date);
					if (!point) return null;
					return {
						name: serie.name,
						...(tooltipLabel === "serie" && { serieName: serie.name }),
						value: roundValueUpTo(point.value),
						time: point.name.includes("T") ? prettifyISODate(point.name) : point.name,
						unit: point.unit
					};
				}),
				symbol: this._symbolShapes[index % this._symbolShapes.length]
			};
		});
	}

	private _sortLineCategories(categories: string[]): string[] {
		const mappedCategories = categories.map(category => ({
			category,
			sortValue: this._getLineCategorySortValue(category)
		}));

		const canSortChronologically = mappedCategories.every(
			mappedCategory => mappedCategory.sortValue !== null
		);

		if (!canSortChronologically) {
			return [...categories].sort();
		}

		return mappedCategories
			.sort((firstCategory, secondCategory) => {
				if (firstCategory.sortValue !== secondCategory.sortValue) {
					return (firstCategory.sortValue || 0) - (secondCategory.sortValue || 0);
				}

				return firstCategory.category.localeCompare(secondCategory.category);
			})
			.map(mappedCategory => mappedCategory.category);
	}

	private _getLineCategorySortValue(category: string): number | null {
		const normalizedCategory = String(category || "").trim();
		const uppercaseCategory = normalizedCategory.toUpperCase();
		const monthOrder = [
			"JAN",
			"FEB",
			"MAR",
			"APR",
			"MAY",
			"JUN",
			"JUL",
			"AUG",
			"SEP",
			"OCT",
			"NOV",
			"DEC"
		];

		const monthIndex = monthOrder.indexOf(uppercaseCategory);
		if (monthIndex !== -1) {
			return monthIndex;
		}

		const monthYearMatch = uppercaseCategory.match(
			/^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[\s/_-]+(\d{2,4})$/
		);
		if (monthYearMatch) {
			const matchedMonth = monthOrder.indexOf(monthYearMatch[1]);
			const year = Number(monthYearMatch[2]);
			const normalizedYear = year < 100 ? 2000 + year : year;
			return normalizedYear * 12 + matchedMonth;
		}

		const parsedDate = Date.parse(normalizedCategory);
		if (!Number.isNaN(parsedDate)) {
			return parsedDate;
		}

		return null;
	}

	/**
	 * Generates series options for a bar chart.
	 *
	 * @param barChart - The bar chart object containing series data.
	 * @param coreSerieOptions - The core series options, which can be a single option or an array of options.
	 * @param isGradient - A boolean indicating whether to use gradient colors.
	 * @param colors - An array of colors to be used for the series.
	 * @param patterns - An array of chart patterns to be applied to the series.
	 * @param shouldBeReversed - A boolean indicating whether the series data should be reversed.
	 * @param tooltipLabel - A string indicating whether the tooltip should display the value or the series name.
	 * @returns An array of series options for the bar chart.
	 */
	private _getSeriesForBar(
		barChart: BarChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined,
		isGradient: boolean,
		isGradientInverted: boolean,
		colors: string[],
		patterns: ChartPattern[],
		shouldBeReversed: boolean,
		tooltipLabel: TooltipLabel
	): SeriesOption[] {
		return barChart.barSeries.map((serie, serieIndex) => ({
			...(Array.isArray(coreSerieOptions)
				? (coreSerieOptions?.[serieIndex] as BarSeriesOption[])
				: (coreSerieOptions as BarSeriesOption)),
			name: serie.name,
			data: reverseConditionally(
				serie.bars.map((bar, barIndex) =>
					Object.keys(bar).length > 0
						? {
								name: bar.name,
								...(tooltipLabel === "serie" && { serieName: serie.name }),
								...(tooltipLabel === "region" && {
									regionName: this._regionsMap[bar.name] || bar.name
								}),
								value: roundValueUpTo(bar.value),
								...(bar.percentageDiff && {
									percentageDiff: roundValueUpTo(bar.percentageDiff)
								}),
								unit: bar.unit,
								itemStyle: {
									color:
										colors[serieIndex] ||
										getBarColor(
											barChart.barSeries,
											serieIndex,
											barIndex,
											isGradient,
											isGradientInverted
										),
									...(this._isAppAccessible &&
										patterns[serieIndex] && { decal: patterns[serieIndex] })
								}
							}
						: {}
				),
				shouldBeReversed
			)
		}));
	}

	/**
	 * Generates the series options for a pie chart.
	 *
	 * @param pieChart - The pie chart data containing slices.
	 * @param coreSerieOptions - The core series options to be extended.
	 * @param colors - An array of colors to be used for the pie slices.
	 * @returns An array of series options for the pie chart.
	 */
	private _getSeriesForPie(
		pieChart: PieChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined,
		colors: string[],
		labelPosition?: "outside" | "inside"
	): SeriesOption[] {
		const totalValue = getPieChartTotalValue(pieChart.PieChartSlice);

		const serieData = pieChart.PieChartSlice.map((slice, sliceIndex) => {
			const labelPos =
				labelPosition || getPieChartLabelPosition(roundValueUpTo(slice.value), totalValue);
			const { labelColor, labelTextBorderColor } = getPieChartLabelColor(
				labelPos,
				colors[sliceIndex]
			);

			return {
				name: slice.name,
				value: roundValueUpTo(slice.value),
				unit: slice.unit,
				itemStyle: {
					color: colors[sliceIndex]
				},
				label: {
					show: true,
					textBorderWidth: 0.5,
					textBorderColor: labelTextBorderColor,
					position: labelPos,
					color: labelColor
				}
			};
		});

		return [
			{
				...(Array.isArray(coreSerieOptions)
					? (coreSerieOptions?.[0] as PieSeriesOption[])
					: (coreSerieOptions as PieSeriesOption)),
				data: serieData as PieSeriesOption["data"]
			}
		];
	}

	/**
	 * Generates series options for a scatter chart.
	 *
	 * @param scatterChart - The scatter chart data containing scatter points and optional thresholds.
	 * @param coreSerieOptions - The core series options to be extended for the scatter chart.
	 * @returns An array of series options for the scatter chart.
	 */
	private _getSeriesForScatter(
		scatterChart: ScatterChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined
	): SeriesOption[] {
		if (!scatterChart.scatterPoints) return [];

		const thresholdX = scatterChart.thresholdX || 0;
		const thresholdY = scatterChart.thresholdY || 0;

		const scatterSerie: SerieDataWithThreshold = {
			data: scatterChart.scatterPoints.map(point =>
				getMappedScatterPoint(thresholdX, thresholdY, point)
			),
			thresholdX,
			thresholdY
		};

		const markLineOptions = buildScatterMarkLineOptions(scatterSerie);

		return [
			{
				...(Array.isArray(coreSerieOptions)
					? (coreSerieOptions?.[0] as ScatterSeriesOption[])
					: (coreSerieOptions as ScatterSeriesOption)),
				data: scatterSerie.data,
				markLine: markLineOptions
			}
		];
	}

	/**
	 * Generates a series of map data for chart visualization based on carbon intensities.
	 *
	 * @param mapChart - The carbon intensities data for different regions.
	 * @param coreSerieOptions - The core series options to be used for the map series.
	 * @param selectedRegion - (Optional) The region that is currently selected.
	 * @returns An array of series options for the map chart.
	 */
	private _getSeriesForMap(
		mapChart: MapChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined
	): SeriesOption[] {
		const pieces = getRangesColorForMapItemAreaColor();
		const serieData = mapChart.mapRegions.map(region => ({
			name: region.name,
			shortName: region.shortName,
			value: roundValueUpTo(region.value),
			unit: region.unit,
			select: { itemStyle: { areaColor: getMapSelectedItemAreaColor(region.value, pieces) } }
		}));

		const mapSeries: MapSeriesOption = {
			...(Array.isArray(coreSerieOptions)
				? (coreSerieOptions?.[0] as MapSeriesOption)
				: (coreSerieOptions as MapSeriesOption)),
			...(mapChart.sitePoints?.length && {
				markPoint: {
					symbol: "circle",
					symbolSize: 14,
					label: { show: false },
					tooltip: {
						formatter: (params: CallbackDataParams) => params.name || ""
					},
					itemStyle: {
						color: COLORS.TURQUOISE_100,
						borderColor: COLORS.WHITE,
						borderWidth: 2
					},
					emphasis: {
						itemStyle: {
							color: COLORS.YELLOW_100,
							borderColor: COLORS.WHITE,
							borderWidth: 2
						}
					},
					data: mapChart.sitePoints.map(point => ({
						name: point.name,
						coord: point.value,
						value: 1
					}))
				}
			}),
			data: serieData as MapSeriesOption["data"]
		};

		return [mapSeries as SeriesOption];
	}

	/**
	 * Generates series options for a mixed chart.
	 *
	 * @param mixedChart - The mixed chart configuration object.
	 * @param coreSerieOptions - The core series options, which can be a single option or an array of options.
	 * @param colors - An array of colors to be used for the series.
	 * @param patterns - An array of chart patterns to be applied to the series.
	 * @returns An array of series options for the mixed chart.
	 */
	private _getSeriesForMixed(
		mixedChart: MixedChart,
		coreSerieOptions: SeriesOption | SeriesOption[] | undefined,
		colors: string[],
		patterns: ChartPattern[]
	): SeriesOption[] {
		return mixedChart.series.map(
			(serie, serieIndex) =>
				({
					...(serie.type === "bar"
						? Array.isArray(coreSerieOptions)
							? (coreSerieOptions?.[serieIndex] as BarSeriesOption[])
							: (coreSerieOptions as BarSeriesOption)
						: Array.isArray(coreSerieOptions)
							? (coreSerieOptions?.[serieIndex] as LineSeriesOption[])
							: (coreSerieOptions as LinesSeriesOption)),
					name: serie.name,
					type: serie.type,
					...(serie.type === "line" && {
						...(colors.length && { color: colors.at(-1) }),
						yAxisIndex: 1
					}),
					data: serie.values.map((value, valueIndex) => ({
						name: serie.type === "line" ? serie.values[valueIndex].name : serie.name,
						value: roundValueUpTo(value.value),
						unit: value.unit,
						...(serie.type === "bar" && {
							itemStyle: {
								...(colors.length && { color: colors[serieIndex] }),
								...(this._isAppAccessible &&
									patterns[serieIndex] && { decal: patterns[serieIndex] })
							}
						})
					}))
				}) as SeriesOption
		);
	}
}
